import React from 'react';

import i18n from '@client/utils/i18n';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectSectionStatus, RecipeSection } from '@ledium/types';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';

import { invertColor, sectionBgColor } from './colorUtils';

const hintStyle = { textAlign: 'center' } as const;

type RecipeSectionDisplay = RecipeSection & {
    sectionColor?: { background?: string; text?: string };
};

interface OnSortArgs {
    oldIndex: number;
    newIndex: number;
}

interface RecipeSectionsProps {
    sectionList: RecipeSectionDisplay[];
    onSort?: (args: OnSortArgs) => void;
    onSectionClick?: (section: RecipeSectionDisplay) => void;
    status?: ProjectSectionStatus | null;
    readOnly?: boolean;
    passive?: boolean;
}

export function getRepeatItemWidth(sectionId: string, repeatNum: number, status: ProjectSectionStatus | null | undefined): number {
    if (!status) {
        return 0;
    }
    if (sectionId < status.id) {
        return 100;
    }
    if (sectionId > status.id) {
        return 0;
    }
    if (repeatNum < status.currentRepeatNum) {
        return 100;
    }
    if (repeatNum > status.currentRepeatNum) {
        return 0;
    }
    return Number(status.sectionPercent);
}

interface SectionBodyProps {
    section: RecipeSectionDisplay;
    status?: ProjectSectionStatus | null;
}

const SectionBody = React.memo(function SectionBody({ section, status }: SectionBodyProps) {
    const theme = useTheme();
    const bgColor = sectionBgColor(section.sectionColor?.background, theme.palette.mode === 'dark');
    const textColor = bgColor ? invertColor(bgColor) : section.sectionColor?.text;

    return (
        <Paper square elevation={0} sx={{ backgroundColor: bgColor, color: textColor }}>
            <Box
                sx={{
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding: '0 5px',
                    lineHeight: '30px',
                    borderBottom: '1px solid',
                    borderBottomColor: 'divider',
                    whiteSpace: 'nowrap'
                }}
            >
                {section.name}
                <Box sx={{ lineHeight: '25px', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {section.repeatNum} x {section.circadianLength} {i18n('circadian_length_hours')}
                </Box>
            </Box>
            <Box sx={{ height: '20px', borderRight: '1px solid', borderRightColor: 'divider' }}>
                {[...Array(section.repeatNum)].map((_, i) => (
                    <Box
                        key={`${section.id}-${i}`}
                        sx={{
                            display: 'inline-block',
                            position: 'relative',
                            borderLeft: '1px solid',
                            borderLeftColor: 'divider',
                            textAlign: 'center',
                            height: '20px',
                            width: `${100 / section.repeatNum}%`
                        }}
                    >
                        {status ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    height: '100%',
                                    background: 'common.black',
                                    opacity: '0.5',
                                    width: `${getRepeatItemWidth(section.id, i + 1, status)}%`
                                }}
                            />
                        ) : null}
                    </Box>
                ))}
            </Box>
        </Paper>
    );
});

interface SectionItemProps {
    section: RecipeSectionDisplay;
    fullLength: number;
    onSectionClick?: (section: RecipeSectionDisplay) => void;
    status?: ProjectSectionStatus | null;
}

const SectionItem = React.memo(function SectionItem({ section, fullLength, onSectionClick, status }: SectionItemProps) {
    return (
        <Box
            component="li"
            data-automation="recipe-section-chip"
            onClick={() => onSectionClick && onSectionClick(section)}
            sx={{ display: 'inline-block', cursor: 'pointer', width: `${(98 / fullLength) * section.repeatNum}%` }}
        >
            <SectionBody section={section} status={status} />
        </Box>
    );
});

interface SortableSectionItemProps extends SectionItemProps {
    disabled: boolean;
}

const SortableSectionItem = React.memo(function SortableSectionItem({ section, disabled, fullLength, onSectionClick, status }: SortableSectionItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id, disabled });
    return (
        <Box
            component="li"
            data-automation="recipe-section-chip"
            ref={setNodeRef}
            sx={{
                display: 'inline-block',
                cursor: 'pointer',
                transform: CSS.Transform.toString(transform),
                transition,
                width: `${(98 / fullLength) * section.repeatNum}%`
            }}
            onClick={() => onSectionClick && onSectionClick(section)}
            {...attributes}
            {...listeners}
        >
            <SectionBody section={section} status={status} />
        </Box>
    );
});

const RecipeSections = ({ sectionList, onSort, onSectionClick, status, readOnly, passive }: RecipeSectionsProps) => {
    const fullLength = sectionList.map((e) => e.repeatNum).reduce((a, c) => a + c, 0);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = sectionList.findIndex((s) => s.id === active.id);
            const newIndex = sectionList.findIndex((s) => s.id === over.id);
            onSort?.({ oldIndex, newIndex });
        }
    }

    const disabled = !onSort || !!readOnly;
    const commonProps = { fullLength, onSectionClick, status };

    if (readOnly || passive) {
        return (
            <Box component="ul" sx={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', p: 0 }}>
                {sectionList.map((section) => (
                    <SectionItem key={section.id} section={section} {...commonProps} />
                ))}
            </Box>
        );
    }

    return (
        <>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sectionList.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
                    <Box component="ul" sx={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'space-between', p: 0 }}>
                        {sectionList.map((section) => (
                            <SortableSectionItem key={section.id} section={section} disabled={disabled} {...commonProps} />
                        ))}
                    </Box>
                </SortableContext>
            </DndContext>
            {sectionList.length > 1 && onSort && !readOnly ? <p style={hintStyle}>{i18n('you_can_change_the_order_of_the_sections_by_drag_n_drop')}</p> : null}
        </>
    );
};

export default RecipeSections;
